import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { createRoleApi, editRoleApi, viewRoleApi } from "../../api/roleApi";

import { getPermissionListApi } from "../../api/permissionApi";
import { MENU_PERMISSION_ORDER } from "../../utils/menuPermissionOrder";
import FullPageLoader from "../../common/FullPageLoader";

/* =========================================================
   Permission Helpers
========================================================= */

const permissionNameOf = (permission) =>
  permission?.permissionName ||
  permission?.name ||
  permission?.key ||
  permission?.code ||
  permission?.title ||
  "";

const normalizePermissionName = (name) =>
  String(name || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const permissionIdOf = (permission) => {
  const id = permission?.permissionId || permission?._id || permission?.id;
  return id && typeof id === "object" ? id._id || id.id || id.permissionId : id;
};

const asPermissionObject = (value) => {
  if (!value) {
    return {};
  }

  if (Array.isArray(value)) {
    return value.reduce((result, permission) => {
      const name = permissionNameOf(permission);

      if (name) {
        result[name] = permission;
      }

      return result;
    }, {});
  }

  if (typeof value === "object") {
    return value;
  }

  return {};
};

const permissionResponseOf = (response) =>
  asPermissionObject(response?.data?.permissions || response?.permissions || response?.data || {});

const rolePermissionsOf = (value) => {
  if (!value) {
    return {};
  }

  if (Array.isArray(value)) {
    return value.reduce((result, permission) => {
      const permissionId = permissionIdOf(permission);

      if (permissionId) {
        result[String(permissionId)] = permission;
      }

      return result;
    }, {});
  }

  if (typeof value === "object") {
    return value;
  }

  return {};
};

/* =========================================================
   Custom Checkbox
========================================================= */

const CustomCheckbox = ({ checked, onChange, disabled = false, label }) => {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        marginBottom: 0,
      }}
    >
      <span
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!disabled) {
            onChange(!checked);
          }
        }}
        style={{
          width: "18px",
          height: "18px",
          minWidth: "18px",
          border: checked ? "2px solid #0d6efd" : "2px solid #6c757d",
          borderRadius: "3px",
          backgroundColor: checked ? "#0d6efd" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          boxSizing: "border-box",
          transition: "all 0.15s ease",
        }}
      >
        {checked && (
          <span
            style={{
              color: "#fff",
              fontSize: "13px",
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            ✓
          </span>
        )}
      </span>

      {label && (
        <span
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {label}
        </span>
      )}
    </label>
  );
};

/* =========================================================
   Group Select Checkbox
========================================================= */

const GroupSelect = ({ permissionList, onChange, disabled }) => {
  const isAllSelected =
    permissionList.length > 0 && permissionList.every((permission) => permission.enabled === true);

  return <CustomCheckbox checked={isAllSelected} onChange={onChange} disabled={disabled} />;
};

/* =========================================================
   Role Form
========================================================= */

const RoleForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get ID from URL
  const { id: routeId } = useParams();

  /*
    Supports both:

    1. navigate("/role-edit/123")

    2. navigate("/role-edit/123", {
         state: {
           roleId: "123"
         }
       })
  */
  const id = location.state?.roleId || routeId || null;

  const isEdit = Boolean(id);

  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState({});

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [roleNameError, setRoleNameError] = useState("");

  /* =========================================================
     Fetch Permission List
  ========================================================= */

  const fetchPermissions = async () => {
    try {
      const response = await getPermissionListApi();

      const permissionObject = permissionResponseOf(response);

      const formatted = Object.fromEntries(
        Object.entries(permissionObject).map(([name, permission]) => [
          name,
          {
            ...permission,

            permissionId: permission?.permissionId || permission?._id || permission?.id,

            enabled: permission?.enabled === true,

            label: permission?.label,
          },
        ]),
      );  

      return formatted;
    } catch (fetchError) {
      console.error("Permission list error:", fetchError);

      setError(fetchError?.response?.data?.message || "Failed to load permissions");

      return {};
    }
  };

  /* =========================================================
     Fetch Role
  ========================================================= */

  const fetchRole = async (permissionList) => {
    try {
      const response = await viewRoleApi(id);

      const role = response?.data?.role || response?.data?.data || response?.data || response?.role;

      if (!role) {
        setError("Role not found");
        return;
      }

      setRoleName(role?.name || "");

      const rolePermissions = rolePermissionsOf(role?.permissions);

      const updatedPermissions = Object.fromEntries(
        Object.entries(permissionList).map(([name, permission]) => {
          const masterPermissionId = String(permissionIdOf(permission) || "");
          const rolePermission =
            rolePermissions[masterPermissionId] ||
            Object.values(rolePermissions).find((item) => {
              const rolePermissionId = permissionIdOf(item);
              return (
                (rolePermissionId && String(rolePermissionId) === masterPermissionId) ||
                normalizePermissionName(permissionNameOf(item)) === normalizePermissionName(name)
              );
            });

          const hasPermission =
            rolePermission === true ||
            rolePermission?.enabled === true ||
            rolePermission?.isEnabled === true;

          return [
            name,
            {
              ...permission,

              permissionId: masterPermissionId,

              enabled: hasPermission,
            },
          ];
        }),
      );

      setPermissions(updatedPermissions);
    } catch (fetchError) {
      console.error("View role error:", fetchError);

      setError(fetchError?.response?.data?.message || fetchError?.message || "Failed to load role");
    }
  };

  /* =========================================================
     Load Form Data
  ========================================================= */

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const permissionList = await fetchPermissions();

        /*
          EDIT
          ----
          Load existing role permissions.

          ADD
          ---
          Reset all permissions to false.
        */

        if (isEdit && id && Object.keys(permissionList).length > 0) {
          await fetchRole(permissionList);
        } else {
          const resetPermissions = Object.fromEntries(
            Object.entries(permissionList).map(([name, permission]) => [
              name,
              {
                ...permission,
                enabled: false,
              },
            ]),
          );

          setPermissions(resetPermissions);
        }
      } catch (loadError) {
        console.error("Load role form error:", loadError);

        setError("Failed to load role data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEdit, id]);

  /* =========================================================
     Individual Permission Change
  ========================================================= */

  const handlePermissionChange = (name) => {
    setPermissions((current) => {
      if (!current[name]) {
        return current;
      }

      return {
        ...current,

        [name]: {
          ...current[name],

          enabled: current[name].enabled !== true,
        },
      };
    });
  };

  /* =========================================================
     Group Permission Change
  ========================================================= */

  const handleGroupChange = (permissionList, enabled) => {
    setPermissions((current) => {
      const updated = {
        ...current,
      };

      permissionList.forEach(({ name }) => {
        if (updated[name]) {
          updated[name] = {
            ...updated[name],
            enabled,
          };
        }
      });

      return updated;
    });
  };

  /* =========================================================
     Submit
  ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      setRoleNameError("");

      const permissionPayload = Object.values(permissions).map((permission) => ({
        permissionId: permissionIdOf(permission),

        enabled: permission?.enabled === true,
      }));

      const payload = {
        name: roleName.trim(),
        permissions: permissionPayload,
      };

      /* =========================
         EDIT
      ========================= */

      const response = id
        ? await editRoleApi(id, payload)
        : await createRoleApi(payload);

      if (response?.success === false || response?.status >= 400) {
        const backendErrors = response?.error || {};
        const nameError = backendErrors?.name;

        setRoleNameError(
          Array.isArray(nameError) ? nameError.join(", ") : nameError || "",
        );
        setError(
          (nameError ? "" : response?.message) ||
            backendErrors?.permissions ||
            "Failed to save role",
        );
        return;
      }

      setTimeout(() => {
        navigate("/role-list");
      }, 1000);
    } catch (submitError) {
      console.error("Role submit error:", submitError);

      const responseData = submitError?.response?.data;
      const backendErrors = responseData?.error;
      const nameError = backendErrors?.name;
      setRoleNameError(
        Array.isArray(nameError) ? nameError.join(", ") : nameError || "",
      );
      setError(
        (nameError ? "" : responseData?.message) ||
          backendErrors?.permissions ||
          submitError?.message ||
          "Failed to save role",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     Group Permissions
  ========================================================= */

  const groupedPermissions = Object.entries(permissions)
    .reduce((groups, [name, permission]) => {
      const groupName = permission.label;

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push({
        name,

        enabled: permission.enabled === true,

        permissionId: permission.permissionId,
      });

      return groups;
    }, {});

  /* =========================================================
     Loading
  ========================================================= */

  if (loading) {
    return <FullPageLoader text="Loading role form..." />;
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="container-fluid py-4">
      {submitting && <FullPageLoader text="Saving role..." />}
      {/* Header */}

      

      {/* Error */}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Success */}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* =================================================
            Role Name
        ================================================= */}

        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <label className="form-label">
              Role Name <span className="text-danger">*</span>
            </label>

            <input
              type="text"
              className={`form-control${roleNameError ? " is-invalid" : ""}`}
              placeholder="Enter role name"
              value={roleName}
              onChange={(event) => {
                setRoleName(event.target.value);
                if (roleNameError) setRoleNameError("");
              }}
              aria-invalid={Boolean(roleNameError)}
              disabled={submitting}
            />
            {roleNameError && (
              <div className="invalid-feedback d-block">{roleNameError}</div>
            )}
          </div>
        </div>

        {/* =================================================
            Permissions
        ================================================= */}

        <div className="card shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">Permissions</h5>
          </div>

          <div className="card-body">
            {Object.keys(groupedPermissions).length === 0 ? (
              <div className="text-center py-4 text-muted">No permissions found</div>
            ) : (
              <div className="row">
                {Object.entries(groupedPermissions).map(([groupName, permissionList]) => (
                  <div className="col-md-3 col-sm-6 mb-4" key={groupName}>
                    {/* Group Header */}

                    <div className="d-flex align-items-center mb-3">
                      {permissionList.length > 1 && (
                        <div className="me-2">
                          <GroupSelect
                            permissionList={permissionList}
                            onChange={(enabled) => handleGroupChange(permissionList, enabled)}
                            disabled={submitting}
                          />
                        </div>
                      )}

                      <h6 className="mb-0 fw-bold">{groupName}</h6>
                    </div>

                    {/* Permission List */}

                    <div>
                      {permissionList.map((permission) => (
                        <div
                          key={permission.permissionId || permission.name}
                          style={{
                            marginBottom: "10px",
                          }}
                        >
                          <CustomCheckbox
                            checked={permission.enabled === true}
                            disabled={submitting}
                            onChange={() => handlePermissionChange(permission.name)}
                            label={permission.name}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* =================================================
            Buttons
        ================================================= */}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || Object.keys(permissions).length === 0}
            aria-busy={submitting}
          >
            {submitting ? "Saving..." : isEdit ? "Update Role" : "Add Role"}
          </button>

          <button
            type="button"
            className="btn"
            style={{
              backgroundColor: "#032B4D",
              color: "#fff",
            }}
            onClick={() => navigate("/role-list")}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoleForm;
